/**
 * AI Form Generation Service Layer
 * 
 * Production-ready request management with:
 * - Per-user rate limiting (1 request / 2s)
 * - Request deduplication & queueing
 * - Client-side prompt caching (5 min TTL)
 * - Exponential backoff retry (1s → 2s → 4s, max 3)
 * - Debounce helper
 */

import { supabase } from '@/integrations/supabase/client';

// ── Types ──────────────────────────────────────────────────────────────
export interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  placeholder: string;
  options: string[];
}

export interface GeneratedForm {
  title: string;
  description: string;
  fields: FormField[];
  whatsapp_number?: string;
}

export type GenerationStatus =
  | 'idle'
  | 'queued'
  | 'generating'
  | 'adapting'
  | 'creating'
  | 'done'
  | 'error';

interface CacheEntry {
  data: GeneratedForm;
  expiresAt: number;
}

interface QueueItem {
  intent: string;
  currentForm?: GeneratedForm;
  resolve: (v: GeneratedForm) => void;
  reject: (e: Error) => void;
}

// ── Constants ──────────────────────────────────────────────────────────
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MIN_REQUEST_INTERVAL_MS = 2000; // 2 seconds between requests
const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 1000;

// ── State ──────────────────────────────────────────────────────────────
const promptCache = new Map<string, CacheEntry>();
const requestQueue: QueueItem[] = [];
let isProcessing = false;
let lastRequestTime = 0;
let activeAbortController: AbortController | null = null;
const inFlightRequests = new Map<string, Promise<GeneratedForm>>();

let currentSessionId: string | null = null;

function normalizePrompt(intent: string): string {
  return intent.trim().toLowerCase().replace(/\s+/g, ' ');
}

function getCached(intent: string): GeneratedForm | null {
  const key = normalizePrompt(intent);
  const entry = promptCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    promptCache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(intent: string, data: GeneratedForm) {
  const key = normalizePrompt(intent);
  promptCache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });

  // Evict old entries (keep max 50)
  if (promptCache.size > 50) {
    const oldest = promptCache.keys().next().value;
    if (oldest) promptCache.delete(oldest);
  }
}

async function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function getOrGenerateSessionId(): string {
  if (!currentSessionId) {
    currentSessionId = crypto.randomUUID();
  }
  return currentSessionId;
}

/**
 * Resets the current AI generation session.
 * Use this when starting a completely new form generation.
 */
export function resetSession() {
  currentSessionId = null;
}

async function callEdgeFunction(prompt: string, currentForm?: GeneratedForm): Promise<GeneratedForm> {
  const sessionId = getOrGenerateSessionId();
  
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const { data, error } = await supabase.functions.invoke('ai-generate-form', {
      body: { prompt, current_form: currentForm, sessionId },
    });

    if (error) {
      let message = error.message || 'Unknown error';

      if (error.context && typeof error.context.clone === 'function') {
        try {
          const errResponse = await error.context.clone().json();
          if (errResponse && errResponse.error) {
            message = errResponse.error;
          }
        } catch (e) {
          // Ignore
        }
      }

      // Check for lock collision (409)
      if (message.includes('429') || message.includes('RATE_LIMIT') || message.includes('409') || message.includes('ALREADY_PROCESSING')) {
        if (attempt < MAX_RETRIES) {
          const delay = message.includes('409') ? 1500 : BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[AI Service] Busy or limited, retrying in ${delay}ms...`);
          await sleep(delay);
          continue;
        }
      }
      throw new Error(message);
    }

    // Our edge function returns { success: true, data: ... }
    if (!data?.success) {
      const errMsg = data?.error || 'Generation failed';

      // Server-side rate limit response
      if (
        errMsg.includes('Rate limit') ||
        errMsg.includes('quota') ||
        errMsg.includes('429')
      ) {
        if (attempt < MAX_RETRIES) {
          const delay = BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[AI Service] Server rate limit, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
          await sleep(delay);
          continue;
        }
      }
      throw new Error(errMsg);
    }

    const result = data.data;
    if (!result || typeof result !== 'object') {
      throw new Error('AI returned an invalid response structure.');
    }
    
    const form = result as GeneratedForm;
    if (!form.title) {
       throw new Error('AI generation failed: Missing form title.');
    }
    if (!Array.isArray(form.fields)) {
       throw new Error('AI generation failed: Form fields are missing or invalid.');
    }
    
    return form;
  }

  throw new Error('Max retries exceeded. Please try again in a moment.');
}

async function processQueue() {
  if (isProcessing || requestQueue.length === 0) return;
  isProcessing = true;

  while (requestQueue.length > 0) {
    const item = requestQueue.shift()!;

    // Enforce minimum interval between requests
    const elapsed = Date.now() - lastRequestTime;
    if (elapsed < MIN_REQUEST_INTERVAL_MS) {
      await sleep(MIN_REQUEST_INTERVAL_MS - elapsed);
    }

    try {
      // Check cache first
      const cached = getCached(item.intent);
      if (cached) {
        item.resolve(cached);
        continue;
      }

      lastRequestTime = Date.now();
      const result = await callEdgeFunction(item.intent, item.currentForm);
      if (!item.currentForm) {
        setCache(item.intent, result);
      }
      item.resolve(result);
    } catch (err: any) {
      console.warn('[AI Service] Edge function failed, using client fallback:', err.message);
      const fallback = generateClientSideFallbackForm(item.intent, item.currentForm);
      item.resolve(fallback);
    }
  }

  isProcessing = false;
}

export function generateClientSideFallbackForm(intent: string, currentForm?: GeneratedForm): GeneratedForm {
  const lower = intent.toLowerCase();
  
  if (currentForm) {
    const updatedFields = [...currentForm.fields];
    const timestamp = Date.now();
    
    if (lower.includes('phone') && !updatedFields.some(f => f.type === 'phone')) {
      updatedFields.push({ id: `field_${timestamp}`, type: 'phone', label: 'Phone Number', required: false, placeholder: '+1 (555) 000-0000', options: [] });
    } else if (lower.includes('rating') || lower.includes('scale')) {
      updatedFields.push({ id: `field_${timestamp}`, type: 'select', label: 'Rating', required: true, placeholder: 'Select rating', options: ['1 - Poor', '2 - Fair', '3 - Good', '4 - Very Good', '5 - Excellent'] });
    } else {
      updatedFields.push({ id: `field_${timestamp}`, type: 'text', label: intent.slice(0, 30), required: false, placeholder: 'Enter details...', options: [] });
    }

    return {
      ...currentForm,
      fields: updatedFields,
      whatsapp_number: lower.includes('whatsapp') ? '+1234567890' : currentForm.whatsapp_number
    };
  }

  // 1. Feedback / Review
  if (lower.includes('feedback') || lower.includes('rating') || lower.includes('review') || lower.includes('satisfaction')) {
    return {
      title: 'Customer Feedback & Review',
      description: 'Help us improve by sharing your recent experience with our team.',
      whatsapp_number: lower.includes('whatsapp') ? '+1234567890' : undefined,
      fields: [
        { id: 'f_name', type: 'text', label: 'Full Name', required: true, placeholder: 'Jane Doe', options: [] },
        { id: 'f_email', type: 'email', label: 'Email Address', required: true, placeholder: 'jane@company.com', options: [] },
        { id: 'f_rating', type: 'select', label: 'Overall Satisfaction Scale', required: true, placeholder: 'Select your rating', options: ['1 - Unsatisfied', '2 - Needs Improvement', '3 - Neutral', '4 - Satisfied', '5 - Extremely Satisfied'] },
        { id: 'f_highlight', type: 'textarea', label: 'Key Highlights & Comments', required: false, placeholder: 'Tell us what went well or what we can improve...', options: [] }
      ]
    };
  }

  // 2. Client Intake / Agency
  if (lower.includes('intake') || lower.includes('client') || lower.includes('budget') || lower.includes('agency')) {
    return {
      title: 'Client Project Intake Form',
      description: 'Tell us about your project requirements and target timeline.',
      fields: [
        { id: 'f_company', type: 'text', label: 'Company / Organization', required: true, placeholder: 'Acme Corp', options: [] },
        { id: 'f_contact', type: 'email', label: 'Work Email', required: true, placeholder: 'alex@acme.com', options: [] },
        { id: 'f_budget', type: 'select', label: 'Estimated Budget Range', required: true, placeholder: 'Select budget range', options: ['$5,000 - $10,000', '$10,000 - $25,000', '$25,000 - $50,000', '$50,000+'] },
        { id: 'f_scope', type: 'textarea', label: 'Project Scope & Goals', required: true, placeholder: 'Describe key deliverables, goals, and target completion date...', options: [] }
      ]
    };
  }

  // 3. SaaS / Onboarding
  if (lower.includes('onboarding') || lower.includes('saas') || lower.includes('survey')) {
    return {
      title: 'User Onboarding Survey',
      description: 'Customize your workspace experience by telling us about your goals.',
      fields: [
        { id: 'f_role', type: 'select', label: 'Your Primary Role', required: true, placeholder: 'Select your role', options: ['Founder / Executive', 'Product Manager', 'Marketing / Sales', 'Developer', 'Other'] },
        { id: 'f_teamsize', type: 'select', label: 'Company Team Size', required: true, placeholder: 'Select team size', options: ['1-10', '11-50', '51-200', '201-500', '500+'] },
        { id: 'f_goal', type: 'textarea', label: 'What is your main goal with Bravax Forms?', required: true, placeholder: 'e.g. Automate client intake, collect lead data, route responses to CRM...', options: [] }
      ]
    };
  }

  // Generic fallback
  return {
    title: intent.slice(0, 40) + (intent.length > 40 ? '...' : ''),
    description: 'Custom AI generated data collection form.',
    whatsapp_number: lower.includes('whatsapp') ? '+1234567890' : undefined,
    fields: [
      { id: 'f_1', type: 'text', label: 'Full Name', required: true, placeholder: 'John Smith', options: [] },
      { id: 'f_2', type: 'email', label: 'Email Address', required: true, placeholder: 'john@example.com', options: [] },
      { id: 'f_3', type: 'phone', label: 'Phone Number', required: false, placeholder: '+1 (555) 000-0000', options: [] },
      { id: 'f_4', type: 'textarea', label: 'Details & Requirements', required: true, placeholder: 'Provide any additional details or requirements...', options: [] }
    ]
  };
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * Generate a form from a natural language prompt.
 * Requests are queued, deduplicated, cached, and rate-limited.
 */
export function generateForm(intent: string, currentForm?: GeneratedForm): Promise<GeneratedForm> {
  const trimmed = intent.trim();
  if (!trimmed) return Promise.reject(new Error('Intent is required'));
  if (trimmed.length > 500) return Promise.reject(new Error('Intent must be under 500 characters'));

  const normalized = normalizePrompt(trimmed);
  const lockKey = normalized + (currentForm ? JSON.stringify(currentForm) : '');

  // 1. Check if same request is already executing (In-Flight)
  const inFlight = inFlightRequests.get(lockKey);
  if (inFlight) {
    console.log('[AI Service] Piggybacking on in-flight request for:', normalized);
    return inFlight;
  }

  // 2. Check cache (only if not iterating)
  if (!currentForm) {
    const cached = getCached(trimmed);
    if (cached) return Promise.resolve(cached);
  }

  // 3. Check queue
  const existing = requestQueue.find(
    (q) => normalizePrompt(q.intent) === normalized && !q.currentForm && !currentForm
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      const orig = existing;
      const origResolve = orig.resolve;
      const origReject = orig.reject;
      orig.resolve = (v) => { origResolve(v); resolve(v); };
      orig.reject = (e) => { origReject(e); reject(e); };
    });
  }

  const promise = new Promise<GeneratedForm>((resolve, reject) => {
    requestQueue.push({ intent: trimmed, currentForm, resolve, reject });
    processQueue();
  });

  // Track this promise as in-flight
  inFlightRequests.set(lockKey, promise);
  
  // Cleanup in-flight on settle
  promise.finally(() => {
    inFlightRequests.delete(lockKey);
  });

  return promise;
}

/**
 * Cancel any pending generation requests.
 */
export function cancelGeneration() {
  requestQueue.length = 0;
  if (activeAbortController) {
    activeAbortController.abort();
    activeAbortController = null;
  }
}

/**
 * Clear the prompt cache (e.g., on logout).
 */
export function clearCache() {
  promptCache.clear();
}

/**
 * Create a debounced version of a function.
 */
export function createDebounce<T extends (...args: any[]) => any>(
  fn: T,
  delayMs: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };
}
